import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, FileText, Loader } from 'lucide-react'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import {
  getApiCompaniesCurrentServicemodulesFilesServiceModuleIdDownload,
  useGetApiCompaniesCurrentServicemodulesFilesServiceModuleId,
  useGetApiCompaniesCurrentServicemodulesFilesServiceModuleIdReadReceipt,
} from '@/api/generated/company-management-user/company-management-user'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Separator } from '@/components/shadcn/separator'
import {
  FileReadReceiptExportDialog
} from "@/pages/service-modules/open-components/file/components/FileReadReceiptExportDialog.tsx";
import type { FileServiceModuleModel, ServiceModuleModel } from '@/api/model'
import { notify } from '@/lib/notify.ts'

interface Props {
  module: ServiceModuleModel
  onClose: () => void
}

const PAGE_SIZE = 10

// Only images get an inline preview; PDF/Excel show a file row instead.
function isImage(name: string | null | undefined): boolean {
  return !!name && /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(name)
}

/**
 * Open dialog of a File module: display only — file preview + download and,
 * if read receipts are active, the paginated read list (who has read it).
 * File management (upload/replace/delete) and the read-receipt toggle
 * go through the pencil → `EditFileServiceModule`.
 */
export function FileServiceModuleDialog({ module, onClose }: Props) {
  const { t } = useTranslation(['service-modules-file', 'service-modules', 'common'])
  const [page, setPage] = useState(0)

  const { data, isLoading, isError } = useGetApiCompaniesCurrentServicemodulesFilesServiceModuleId(module.id)
  const detail = ((data as any)?.data ?? data) as FileServiceModuleModel | undefined

  const needsReadReceipt = !!detail?.needsReadReceipt
  const readReceipt = useGetApiCompaniesCurrentServicemodulesFilesServiceModuleIdReadReceipt(
    module.id,
    { Offset: page * PAGE_SIZE, Limit: PAGE_SIZE },
    { query: { enabled: needsReadReceipt } },
  )
  const receipt = ((readReceipt.data as any)?.data ?? readReceipt.data) as
    | { totalCount: number; items?: { employeeId: string | null; firstName: string | null; lastName: string | null; readAt?: string | null; isRead?: boolean }[] | null }
    | undefined
  const items = receipt?.items ?? []
  const totalPages = Math.max(1, Math.ceil((receipt?.totalCount ?? 0) / PAGE_SIZE))
  const [exportOpen, setExportOpen] = useState(false)
  const hasFile = !!detail?.downloadUrl || !!detail?.path


  const [isDownloading, setIsDownloading] = useState(false)

  // Downloads the file via our own backend endpoint, which streams it from Azure Blob Storage.
  // This avoids CORS issues with direct Azure SAS URLs and allows us to set the correct filename
  // from the database instead of the blob name.
  async function handleOpen() {
    if (isDownloading) return
    setIsDownloading(true)
    try {
      const response = await getApiCompaniesCurrentServicemodulesFilesServiceModuleIdDownload(module.id)
      const url = URL.createObjectURL(response.data)
      const a = document.createElement('a')
      a.href = url
      a.download = detail?.filename ?? 'download'
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      notify.apiError(error, t('openFile.downloadError'))
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="size-7 rounded"
              style={{ backgroundColor: module.colorHex || '#64748b' }}
            />
            {module.name}
          </DialogTitle>
          <DialogDescription>{t('openFile.description')}</DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <LoaderGeneral isLoading loadingMessage={t('openFile.loading')} />
        ) : isError ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('openFile.loadError')}
          </p>
        ) : (
          <div className="flex flex-col gap-5">
            {/* Section 1: file */}
            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold">{t('openFile.fileSection')}</h3>

              {hasFile ? (
                <div className="flex flex-col gap-3">
                  {!isImage(detail?.filename || detail?.downloadUrl || detail?.path) ? (
                    <div className="flex items-center gap-3 rounded-md border p-3">
                      <FileText className="size-8 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate flex-1">{detail?.filename || 'Dokument'}</span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-center rounded-md border bg-muted/40 p-2">
                        <img
                          src={detail?.downloadUrl ?? detail!.path!}
                          alt={detail?.filename || 'Datei'}
                          className="max-h-64 max-w-full object-contain rounded"
                        />
                      </div>
                      {detail?.filename && (
                        <span className="text-xs text-muted-foreground text-center truncate">{detail.filename}</span>
                      )}
                    </div>
                  )}

                  <div>
                    <Button variant="outline" size="sm" onClick={handleOpen} disabled={isDownloading}>
                      {isDownloading
                        ? <Loader className="size-4 mr-1 animate-spin" />
                        : <Download className="size-4 mr-1" />
                      }
                      {t('openFile.download')}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="py-6 text-center text-sm text-muted-foreground">{t('openFile.noFile')}</p>
              )}
            </section>

            {/* Section 2: read receipt (display only) */}
            {needsReadReceipt && (
              <>
                <Separator />
                <section className="flex flex-col gap-3">

                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold">{t('openFile.readReceiptSection')}</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExportOpen(true)}
                    >
                      <Download className="size-4 mr-1" />
                      {t('openFile.exportReadReceipt')}
                    </Button>
                  </div>

                  <div className="flex flex-col gap-2">
                    {readReceipt.isLoading ? (
                      <LoaderGeneral isLoading loadingMessage={t('openFile.loadingList')} />
                    ) : items.length === 0 ? (
                      <p className="py-6 text-center text-sm text-muted-foreground">{t('openFile.noEmployees')}</p>
                    ) : (
                      <>
                        <div className="rounded-md border divide-y">
                          <div className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 text-xs font-medium text-muted-foreground">
                            <span>{t('openFile.columns.employee')}</span>
                            <span className="w-40">{t('openFile.columns.timestamp')}</span>
                            <span className="w-20 text-center">{t('openFile.columns.status')}</span>
                          </div>
                          {items.map((item) => (
                            <div
                              key={item.employeeId ?? `${item.firstName}-${item.lastName}`}
                              className="grid grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 text-sm items-center"
                            >
                              <span className="truncate">{item.firstName} {item.lastName}</span>
                              <span className="w-40 text-muted-foreground text-xs">
                                {item.readAt ? new Date(item.readAt + 'Z').toLocaleString() : '—'}
                              </span>
                              <span className="w-20 flex justify-center">
                                <span
                                  className={
                                    'inline-block rounded px-1.5 py-0.5 text-[10px] text-white ' +
                                    (item.isRead ? 'bg-emerald-600' : 'bg-rose-600')
                                  }
                                >
                                  {item.isRead ? t('openFile.read') : t('openFile.unread')}
                                </span>
                              </span>
                            </div>
                          ))}
                        </div>

                        {totalPages > 1 && (
                          <div className="flex items-center justify-between">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page === 0}
                              onClick={() => setPage((p) => Math.max(0, p - 1))}
                            >
                              {t('openFile.previous')}
                            </Button>
                            <span className="text-xs text-muted-foreground">
                              {t('openFile.pageOf', { page: page + 1, total: totalPages })}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={page + 1 >= totalPages}
                              onClick={() => setPage((p) => p + 1)}
                            >
                              {t('openFile.next')}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </section>
                <FileReadReceiptExportDialog
                  open={exportOpen}
                  onOpenChange={setExportOpen}
                  serviceModuleId={module.id}
                  fileName={detail?.filename}
                />
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('buttons.close', { ns: 'common' })}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
